<template>
  <section id="techstack" class="py-20 bg-background">
    <div class="container mx-auto px-4">
      <div class="text-center mb-16">
        <h2 class="text-4xl font-bold mb-4 text-foreground">
          Tecnologías que Dominamos
        </h2>
        <p class="text-xl text-muted-foreground max-w-3xl mx-auto">
          Nuestro stack tecnológico completo para impulsar tu transformación digital
        </p>
      </div>

      <!-- Carousel Container -->
      <div class="relative max-w-6xl mx-auto">
        <!-- Carousel Content -->
        <div class="overflow-hidden">
          <div 
            class="flex transition-transform duration-500 ease-in-out"
            :style="{ transform: `translateX(-${currentSlide * (100 / visibleCards)}%)` }"
          >
            <div 
              v-for="(category, index) in techCategories"
              :key="index"
              class="flex-shrink-0 px-2"
              :style="{ width: `${100 / visibleCards}%` }"
            >
              <div class="h-full bg-card border-border hover:shadow-lg transition-shadow duration-300 rounded-lg border p-6 flex flex-col">
                <div class="flex flex-col h-full">
                  <div class="flex items-center mb-4">
                    <div :class="`p-3 rounded-lg ${category.bgColor} mr-4 flex-shrink-0`">
                      <component :is="category.icon" :class="`w-6 h-6 ${category.color}`" />
                    </div>
                    <h3 class="text-lg font-semibold text-foreground leading-tight">
                      {{ category.title }}
                    </h3>
                  </div>
                  
                  <div class="flex-1">
                    <div class="flex flex-wrap gap-2">
                      <span 
                        v-for="(tech, techIndex) in category.technologies"
                        :key="techIndex"
                        class="text-xs bg-muted hover:bg-forza-orange/20 hover:text-forza-orange transition-colors duration-200 px-2 py-1 rounded-full whitespace-nowrap"
                      >
                        {{ tech }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <button
          @click="prevSlide"
          class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-card border border-border rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 z-10"
          :disabled="currentSlide === 0"
          :class="{ 'opacity-50 cursor-not-allowed': currentSlide === 0 }"
        >
          <ChevronLeft class="w-5 h-5" />
        </button>

        <button
          @click="nextSlide"
          class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-card border border-border rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 z-10"
          :disabled="currentSlide >= maxSlide"
          :class="{ 'opacity-50 cursor-not-allowed': currentSlide >= maxSlide }"
        >
          <ChevronRight class="w-5 h-5" />
        </button>

        <!-- Dots Indicator -->
        <div class="flex justify-center mt-8 space-x-2">
          <button
            v-for="(_, index) in Math.ceil(techCategories.length / visibleCards)"
            :key="index"
            @click="currentSlide = index"
            :class="[
              'w-3 h-3 rounded-full transition-colors duration-300',
              index === currentSlide ? 'bg-primary' : 'bg-muted'
            ]"
          />
        </div>
      </div>

      <div class="mt-16 text-center">
        <p class="text-muted-foreground">
          <span class="font-semibold text-forza-orange">20+ años de experiencia</span> de nuestros socios implementando soluciones tecnológicas para PyMEs mexicanas
        </p>
        <div class="mt-4 text-sm text-muted-foreground">
          Desliza para ver más tecnologías →
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { 
  Building2, 
  Cloud, 
  Database, 
  Zap, 
  Package, 
  BarChart3, 
  ShoppingCart, 
  Shield, 
  Code, 
  Award,
  ChevronLeft,
  ChevronRight
} from 'lucide-vue-next'

const currentSlide = ref(0)
const visibleCards = ref(3)

const techCategories = [
  {
    icon: Building2,
    title: "ERP y Sistemas Empresariales",
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    technologies: [
      "SAP (R/3, SAP B1, SAP BO, SAP HANA)",
      "Odoo",
      "Intelisis",
      "Enkontrol", 
      "Dynamics 365 (F&O y Business Central)",
      "Oracle (BO y BI)",
      "Inbroker Re"
    ]
  },
  {
    icon: Cloud,
    title: "Infraestructura & Cloud",
    color: "text-sky-500",
    bgColor: "bg-sky-500/10",
    technologies: [
      "Google Cloud Platform (GCP)",
      "AWS (Amazon Web Services)",
      "VMware y Hyper-V",
      "Cisco (LAN, WAN, ISE NAC)",
      "Fortinet",
      "Microsoft (AD, Exchange, SharePoint)",
      "Asterisk, Maxcom",
      "Veritas Backup Exec",
      "SAN (Hitachi y EMC)"
    ]
  },
  {
    icon: Database,
    title: "Bases de Datos",
    color: "text-green-500",
    bgColor: "bg-green-500/10",
    technologies: [
      "SQL Server (2008, 2012)",
      "Sybase",
      "SAP HANA",
      "PostgreSQL",
      "ETL Design & Execution",
      "Replicación y Respaldos"
    ]
  },
  {
    icon: Zap,
    title: "Middleware & Integraciones",
    color: "text-yellow-500",
    bgColor: "bg-yellow-500/10",
    technologies: [
      "MuleSoft",
      "ETL (diseño y ejecución)",
      "Integración POS-ERP",
      "APIs y Sistemas Embebidos",
      "Sistemas Back Office"
    ]
  },
  {
    icon: Package,
    title: "POS, Retail & WMS",
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
    technologies: [
      "NCR POS",
      "Intelisis POS",
      "Auditores de Punto de Venta",
      "Warehouse Management System",
      "OMS y Fulfillment",
      "PIM/MDM",
      "Merchandising Tools"
    ]
  },
  {
    icon: BarChart3,
    title: "BI & Analítica",
    color: "text-indigo-500",
    bgColor: "bg-indigo-500/10",
    technologies: [
      "Power BI",
      "SAP Business Objects",
      "SQL Analysis Services",
      "SSRS (Reporting Services)",
      "Google Analytics",
      "Data Studio"
    ]
  },
  {
    icon: ShoppingCart,
    title: "eCommerce",
    color: "text-pink-500",
    bgColor: "bg-pink-500/10",
    technologies: [
      "VTEX",
      "Adobe Commerce (Magento)",
      "Shopify",
      "Integraciones Omnicanal",
      "SEO, SEM y Trazabilidad"
    ]
  },
  {
    icon: Shield,
    title: "Ciberseguridad",
    color: "text-red-500",
    bgColor: "bg-red-500/10",
    technologies: [
      "Firewalls (Cisco, Fortinet, Juniper)",
      "Kaspersky",
      "Seguridad Perimetral",
      "IAM / Gestión de Identidades",
      "Políticas de Acceso",
      "Proyectos Integrales"
    ]
  },
  {
    icon: Code,
    title: "Desarrollo & Programación",
    color: "text-emerald-500",
    bgColor: "bg-emerald-500/10",
    technologies: [
      "Vue.js",
      "React",
      "HTML/CSS/JavaScript",
      "Docker",
      "Elasticsearch",
      "SAP ABAP"
    ]
  },
  {
    icon: Award,
    title: "Metodologías & Certificaciones",
    color: "text-amber-500",
    bgColor: "bg-amber-500/10",
    technologies: [
      "ITIL v3",
      "PMI",
      "Agile / SCRUM",
      "Google Project Management",
      "Coursera Foundations"
    ]
  }
]

const maxSlide = computed(() => {
  return Math.max(0, Math.ceil(techCategories.length / visibleCards.value) - 1)
})

const nextSlide = () => {
  if (currentSlide.value < maxSlide.value) {
    currentSlide.value++
  }
}

const prevSlide = () => {
  if (currentSlide.value > 0) {
    currentSlide.value--
  }
}

const updateVisibleCards = () => {
  if (window.innerWidth < 768) {
    visibleCards.value = 1
  } else if (window.innerWidth < 1024) {
    visibleCards.value = 2
  } else {
    visibleCards.value = 3
  }
  
  // Adjust current slide if needed
  if (currentSlide.value > maxSlide.value) {
    currentSlide.value = maxSlide.value
  }
}

let autoSlideInterval: number | null = null

const startAutoSlide = () => {
  autoSlideInterval = setInterval(() => {
    if (currentSlide.value >= maxSlide.value) {
      currentSlide.value = 0
    } else {
      currentSlide.value++
    }
  }, 4000)
}

const stopAutoSlide = () => {
  if (autoSlideInterval) {
    clearInterval(autoSlideInterval)
    autoSlideInterval = null
  }
}

onMounted(() => {
  updateVisibleCards()
  window.addEventListener('resize', updateVisibleCards)
  startAutoSlide()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateVisibleCards)
  stopAutoSlide()
})
</script>